/**
 * useChallengeDetail Hook
 * Fetches a single challenge with its submissions for admin management
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { logChallengeAction } from "@/lib/activity-log";
import { createInAppNotification, scheduleLocalNotification } from "@/lib/notifications";
import {
  Challenge,
  ChallengeCompletion,
  ChallengeStatus,
  CompletionStatus,
  ChallengeSubmission,
} from "../types";

/**
 * Extended completion with profile info
 */
export interface CompletionWithProfile extends ChallengeCompletion {
  profile?: {
    id: string;
    full_name: string | null;
    nickname: string | null;
    avatar_url: string | null;
  };
  team?: {
    id: string;
    name: string;
  };
}

interface UseChallengeDetailReturn {
  challenge: Challenge | null;
  submissions: CompletionWithProfile[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateStatus: (newStatus: ChallengeStatus) => Promise<boolean>;
  updateChallenge: (updates: Partial<Challenge>) => Promise<boolean>;
  deleteChallenge: () => Promise<boolean>;
  reviewSubmission: (
    submissionId: string,
    status: CompletionStatus,
    rejectionReason?: string,
    customPoints?: number
  ) => Promise<boolean>;
  stats: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
  };
}

/**
 * Trigger event reveal when a challenge is completed.
 * Per-team: appends teamId to revealed_for_team_ids so only that team sees the reveal.
 * Falls back to global is_revealed=true when no teamId is provided.
 */
async function triggerEventReveal(
  challengeId: string,
  eventId: string,
  revealTargets: string[],
  teamId?: string | null
): Promise<void> {
  try {
    const { data: event, error: eventError } = await supabase
      .from("agenda_events")
      .select("id, is_revealed, title, revealed_for_team_ids")
      .eq("id", eventId)
      .single();

    if (eventError || !event) {
      console.warn("Could not fetch event for reveal:", eventError);
      return;
    }

    if (event.is_revealed) return;

    if (teamId) {
      const currentIds: string[] = event.revealed_for_team_ids || [];
      if (currentIds.includes(teamId)) return;

      const { error: updateError } = await supabase
        .from("agenda_events")
        .update({
          revealed_for_team_ids: [...currentIds, teamId],
          updated_at: new Date().toISOString(),
        })
        .eq("id", eventId);

      if (updateError) {
        console.error("Error revealing event for team:", updateError);
        return;
      }

      console.log(`Event "${event.title}" revealed for team ${teamId}`);
    } else {
      const { error: updateError } = await supabase
        .from("agenda_events")
        .update({
          is_revealed: true,
          updated_at: new Date().toISOString(),
        })
        .eq("id", eventId);

      if (updateError) {
        console.error("Error revealing event:", updateError);
        return;
      }

      console.log(`Event "${event.title}" revealed globally`);
    }
  } catch (err) {
    console.error("Error triggering event reveal:", err);
  }
}

/**
 * Hook for managing a single challenge and its submissions
 */
export const useChallengeDetail = (challengeId: string): UseChallengeDetailReturn => {
  const { profile } = useAuth();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [submissions, setSubmissions] = useState<CompletionWithProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch challenge details and submissions
   */
  const fetchChallenge = useCallback(async () => {
    if (!challengeId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch challenge
      const { data: challengeData, error: challengeError } = await supabase
        .from("challenges")
        .select("*")
        .eq("id", challengeId)
        .single();

      if (challengeError) throw challengeError;
      setChallenge(challengeData as Challenge);

      // For opdracht type challenges, fetch section_completions instead
      if (challengeData.challenge_type === "opdracht") {
        // First get all section IDs for this challenge
        const { data: sectionsData } = await supabase
          .from("challenge_sections")
          .select("id, title")
          .eq("challenge_id", challengeId);

        if (sectionsData && sectionsData.length > 0) {
          const sectionIds = sectionsData.map((s) => s.id);
          const sectionTitleMap: Record<string, string> = {};
          sectionsData.forEach((s) => {
            sectionTitleMap[s.id] = s.title;
          });

          // Fetch section completions
          const { data: sectionCompletions, error: scError } = await supabase
            .from("section_completions")
            .select(`
              *,
              profile:profiles!section_completions_completed_by_fkey(
                id,
                full_name,
                nickname,
                avatar_url
              ),
              team:teams!section_completions_team_id_fkey(
                id,
                name
              )
            `)
            .in("section_id", sectionIds)
            .order("completed_at", { ascending: false });

          if (scError) {
            console.warn("Error fetching section completions:", scError);
            // Try without joins
            const { data: basicSC } = await supabase
              .from("section_completions")
              .select("*")
              .in("section_id", sectionIds)
              .order("completed_at", { ascending: false });

            // Transform section completions to match CompletionWithProfile structure
            const transformed = (basicSC || []).map((sc: any) => ({
              ...sc,
              challenge_id: challengeId,
              section_title: sectionTitleMap[sc.section_id] || "Sectie",
              is_section_completion: true,
            }));
            setSubmissions(transformed as CompletionWithProfile[]);
          } else {
            // Transform section completions to match CompletionWithProfile structure
            const transformed = (sectionCompletions || []).map((sc: any) => ({
              ...sc,
              challenge_id: challengeId,
              section_title: sectionTitleMap[sc.section_id] || "Sectie",
              is_section_completion: true,
            }));
            setSubmissions(transformed as CompletionWithProfile[]);
          }
        } else {
          setSubmissions([]);
        }
      } else {
        // Fetch regular challenge completions
        const { data: submissionsData, error: submissionsError } = await supabase
          .from("challenge_completions")
          .select(`
            *,
            profile:profiles!challenge_completions_completed_by_fkey(
              id,
              full_name,
              nickname,
              avatar_url
            ),
            team:teams!challenge_completions_team_id_fkey(
              id,
              name
            )
          `)
          .eq("challenge_id", challengeId)
          .order("completed_at", { ascending: false });

        if (submissionsError) {
          console.warn("Error fetching submissions:", submissionsError);
          // Try without joins
          const { data: basicSubmissions } = await supabase
            .from("challenge_completions")
            .select("*")
            .eq("challenge_id", challengeId)
            .order("completed_at", { ascending: false });

          setSubmissions((basicSubmissions || []) as CompletionWithProfile[]);
        } else {
          setSubmissions((submissionsData || []) as CompletionWithProfile[]);
        }
      }
    } catch (err: any) {
      console.error("Error fetching challenge:", err);
      setError(err.message || "Kon uitdaging niet laden");
    } finally {
      setIsLoading(false);
    }
  }, [challengeId]);

  /**
   * Update challenge status (activate/deactivate)
   * Sends notifications when a challenge becomes active
   */
  const updateStatus = useCallback(
    async (newStatus: ChallengeStatus): Promise<boolean> => {
      if (!challengeId || !challenge) return false;

      const wasActive = challenge.status === "active";
      const isBecomingActive = newStatus === "active" && !wasActive;

      try {
        const { error: updateError } = await supabase
          .from("challenges")
          .update({
            status: newStatus,
            updated_at: new Date().toISOString(),
          })
          .eq("id", challengeId);

        if (updateError) throw updateError;

        // Send notifications when challenge becomes active
        if (isBecomingActive) {
          await sendChallengeActiveNotifications(challenge);
        }

        // Update local state
        setChallenge((prev) =>
          prev ? { ...prev, status: newStatus } : null
        );

        return true;
      } catch (err: any) {
        console.error("Error updating status:", err);
        setError(err.message || "Kon status niet bijwerken");
        return false;
      }
    },
    [challengeId, challenge]
  );

  /**
   * Send notifications to users when a challenge becomes active
   */
  const sendChallengeActiveNotifications = async (challenge: Challenge) => {
    try {
      // Get list of users to notify based on challenge visibility
      let userIds: string[] = [];

      if (challenge.visibility === "all") {
        // Notify all users in the active weekend
        const { data: teamMembers } = await supabase
          .from("team_members")
          .select("user_id, teams!inner(weekend_id)")
          .eq("teams.weekend_id", challenge.weekend_id);

        userIds = (teamMembers || []).map((tm: any) => tm.user_id);
      } else if (challenge.visibility === "specific_team" && challenge.visible_to_team_id) {
        // Notify only users in the specific team
        const { data: teamMembers } = await supabase
          .from("team_members")
          .select("user_id")
          .eq("team_id", challenge.visible_to_team_id);

        userIds = (teamMembers || []).map((tm: any) => tm.user_id);
      } else if (challenge.visibility === "specific_users" && challenge.visible_to_user_ids?.length) {
        userIds = challenge.visible_to_user_ids;
      }

      // Remove duplicates
      userIds = [...new Set(userIds)];

      // Format deadline text
      let deadlineText = "";
      if (challenge.available_until) {
        const deadline = new Date(challenge.available_until);
        const options: Intl.DateTimeFormatOptions = { 
          day: "numeric", 
          month: "short", 
          hour: "2-digit", 
          minute: "2-digit" 
        };
        deadlineText = `Deadline: ${deadline.toLocaleDateString("nl-NL", options)}`;
      }

      // Send notification to each user
      for (const userId of userIds) {
        await createInAppNotification({
          userId,
          title: "🎯 Nieuwe uitdaging!",
          body: `"${challenge.title}" is nu beschikbaar!${challenge.points ? ` Verdien ${challenge.points} punten!` : ""}${deadlineText ? `\n${deadlineText}` : ""}`,
          type: "challenge_available",
          actionUrl: `/(tabs)/games/challenge/${challenge.id}`,
          actionData: { 
            challengeId: challenge.id,
            challengeType: challenge.challenge_type,
            points: challenge.points,
            deadline: challenge.available_until,
          },
          senderId: profile?.id,
          lottieAnimation: challenge.notification_lottie || "confetti",
        });
      }

      // Also send a local push notification
      await scheduleLocalNotification({
        title: "🎯 Nieuwe uitdaging beschikbaar!",
        body: `"${challenge.title}" is nu beschikbaar${challenge.points ? ` - ${challenge.points} punten!` : "!"}`,
        data: { type: "challenge_available", challengeId: challenge.id },
        lottieAnimation: challenge.notification_lottie || "confetti",
      });

      console.log(`Sent challenge notifications to ${userIds.length} users`);
    } catch (err) {
      console.error("Error sending challenge notifications:", err);
      // Don't throw - notification failure shouldn't block status update
    }
  };

  /**
   * Update challenge details
   */
  const updateChallenge = useCallback(
    async (updates: Partial<Challenge>): Promise<boolean> => {
      if (!challengeId) return false;

      try {
        const { error: updateError } = await supabase
          .from("challenges")
          .update({
            ...updates,
            updated_at: new Date().toISOString(),
          })
          .eq("id", challengeId);

        if (updateError) throw updateError;

        await fetchChallenge();
        return true;
      } catch (err: any) {
        console.error("Error updating challenge:", err);
        setError(err.message || "Kon uitdaging niet bijwerken");
        return false;
      }
    },
    [challengeId, fetchChallenge]
  );

  /**
   * Delete challenge
   */
  const deleteChallenge = useCallback(async (): Promise<boolean> => {
    if (!challengeId) return false;

    try {
      const { error: deleteError } = await supabase
        .from("challenges")
        .delete()
        .eq("id", challengeId);

      if (deleteError) throw deleteError;

      return true;
    } catch (err: any) {
      console.error("Error deleting challenge:", err);
      setError(err.message || "Kon uitdaging niet verwijderen");
      return false;
    }
  }, [challengeId]);

  /**
   * Review a submission (approve/reject)
   */
  const reviewSubmission = useCallback(
    async (
      submissionId: string,
      status: CompletionStatus,
      rejectionReason?: string,
      customPoints?: number
    ): Promise<boolean> => {
      if (!profile?.id) return false;

      try {
        // Check if this is a section completion
        const submission = submissions.find((s) => s.id === submissionId);
        const isSectionCompletion = (submission as any)?.is_section_completion === true;

        const updates: Record<string, unknown> = {
          status,
          reviewed_by: profile.id,
          reviewed_at: new Date().toISOString(),
        };

        if (status === "rejected" && rejectionReason) {
          updates.rejection_reason = rejectionReason;
        }

        // If approved, assign points (use custom points if provided)
        if (status === "approved") {
          // For section completions, get points from section or use customPoints
          if (isSectionCompletion) {
            updates.points_earned = customPoints !== undefined ? customPoints : (submission as any)?.points || 0;
          } else {
            updates.points_earned = customPoints !== undefined ? customPoints : (challenge?.points || 0);
          }
        }

        // Update the correct table based on submission type
        const tableName = isSectionCompletion ? "section_completions" : "challenge_completions";
        const { error: reviewError } = await supabase
          .from(tableName)
          .update(updates)
          .eq("id", submissionId);

        if (reviewError) throw reviewError;

        const pointsAwarded = customPoints !== undefined ? customPoints : (challenge?.points || 0);

        // Get the user who submitted (submission already fetched above)
        const submittedByUserId = submission?.completed_by;

        // Log the review action
        if (challenge) {
          await logChallengeAction(
            profile.id,
            challenge.id,
            challenge.title,
            status === "approved" ? "approve" : "reject",
            {
              submission_id: submissionId,
              points_awarded: status === "approved" ? pointsAwarded : 0,
              rejection_reason: rejectionReason,
              submitted_by: submittedByUserId,
            }
          );
        }

        // Send notification to the user who submitted (if approved)
        if (status === "approved" && submittedByUserId && challenge) {
          // Record points in score_history and update team total_score
          let teamId = submission?.team_id;
          
          // If no team_id on submission, look up user's current team
          if (!teamId && submittedByUserId) {
            const { data: memberData } = await supabase
              .from("team_members")
              .select("team_id")
              .eq("user_id", submittedByUserId)
              .maybeSingle();
            
            teamId = memberData?.team_id || null;
            console.log(`Looked up team for user ${submittedByUserId}: ${teamId}`);
          }
          
          if (teamId && pointsAwarded > 0) {
            // Get current team score
            const { data: teamData, error: teamFetchError } = await supabase
              .from("teams")
              .select("total_score")
              .eq("id", teamId)
              .single();

            if (teamFetchError) {
              console.error("Error fetching team data:", teamFetchError);
            } else {
              const previousTotal = teamData?.total_score || 0;
              const newTotal = previousTotal + pointsAwarded;

              // Insert into score_history
              const { error: historyError } = await supabase.from("score_history").insert({
                team_id: teamId,
                points: pointsAwarded,
                previous_total: previousTotal,
                new_total: newTotal,
                reason: `Uitdaging voltooid: ${challenge.title}`,
                game_id: challenge.id,
                game_type: "challenge",
                awarded_by: profile.id,
                awarded_to: submittedByUserId,
              });

              if (historyError) {
                console.error("Error inserting score_history:", historyError);
              } else {
                console.log(`Score history recorded: +${pointsAwarded} for team ${teamId}`);
              }

              // Update team total_score
              const { error: updateError } = await supabase
                .from("teams")
                .update({ total_score: newTotal })
                .eq("id", teamId);

              if (updateError) {
                console.error("Error updating team score:", updateError);
              } else {
                console.log(`Team ${teamId} score updated to ${newTotal}`);
              }
            }
          } else if (pointsAwarded > 0) {
            console.warn(`Cannot record score: User ${submittedByUserId} is not on a team`);
          }

          // Create in-app notification with confetti animation
          await createInAppNotification({
            userId: submittedByUserId,
            title: "🎉 Inzending goedgekeurd!",
            body: `Je inzending voor "${challenge.title}" is goedgekeurd! Je hebt ${pointsAwarded} punten verdiend.`,
            type: "challenge_approved",
            actionUrl: `/games/challenge/${challenge.id}`,
            actionData: { challengeId: challenge.id, points: pointsAwarded },
            senderId: profile.id,
            lottieAnimation: "confetti",
          });

          // Also send a local push notification
          await scheduleLocalNotification({
            title: "🎉 Inzending goedgekeurd!",
            body: `Je inzending voor "${challenge.title}" is goedgekeurd! +${pointsAwarded} punten`,
            data: { type: "challenge_approved", challengeId: challenge.id },
            lottieAnimation: "confetti",
          });
        }

        // Send notification if rejected
        if (status === "rejected" && submittedByUserId && challenge) {
          await createInAppNotification({
            userId: submittedByUserId,
            title: "Inzending afgekeurd",
            body: rejectionReason 
              ? `Je inzending voor "${challenge.title}" is afgekeurd: ${rejectionReason}`
              : `Je inzending voor "${challenge.title}" is afgekeurd. Probeer het opnieuw!`,
            type: "challenge_rejected",
            actionUrl: `/games/challenge/${challenge.id}`,
            actionData: { challengeId: challenge.id },
            senderId: profile.id,
          });
        }

        // Update local state
        setSubmissions((prev) =>
          prev.map((s) =>
            s.id === submissionId
              ? {
                  ...s,
                  status,
                  reviewed_by: profile.id,
                  reviewed_at: new Date().toISOString(),
                  rejection_reason: rejectionReason || null,
                  points_earned: status === "approved" ? pointsAwarded : 0,
                }
              : s
          )
        );

        // If approved and challenge is linked to an event, trigger reveal
        if (status === "approved" && challenge?.linked_event_id) {
          await triggerEventReveal(
            challenge.id,
            challenge.linked_event_id,
            challenge.reveal_targets || [],
            submission?.team_id
          );
        }

        return true;
      } catch (err: any) {
        console.error("Error reviewing submission:", err);
        setError(err.message || "Kon inzending niet beoordelen");
        return false;
      }
    },
    [profile?.id, challenge, submissions]
  );

  // Calculate stats
  const stats = {
    total: submissions.length,
    pending: submissions.filter((s) => s.status === "pending").length,
    approved: submissions.filter(
      (s) => s.status === "approved" || s.status === "auto_approved"
    ).length,
    rejected: submissions.filter((s) => s.status === "rejected").length,
  };

  // Initial fetch
  useEffect(() => {
    fetchChallenge();
  }, [fetchChallenge]);

  // Subscribe to real-time updates for submissions
  useEffect(() => {
    if (!challengeId) return;

    const channel = supabase
      .channel(`challenge-${challengeId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "challenge_completions",
          filter: `challenge_id=eq.${challengeId}`,
        },
        () => {
          fetchChallenge();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [challengeId, fetchChallenge]);

  return {
    challenge,
    submissions,
    isLoading,
    error,
    refetch: fetchChallenge,
    updateStatus,
    updateChallenge,
    deleteChallenge,
    reviewSubmission,
    stats,
  };
};

export default useChallengeDetail;

